import { createFileRoute, Link, useRouter } from '@tanstack/react-router';
import {
    AlertTriangle,
    CheckCircle2,
    ExternalLink,
    Loader2,
    Phone,
    User,
    Users,
} from 'lucide-react';
import React, { useState } from 'react';
import { z } from 'zod';

import { AdminLayoutWithDetails } from '@apps/web/components/admin/admin_layout_with_details';
import { Badge } from '@apps/web/components/ui/badge';
import { Button } from '@apps/web/components/ui/button';
import { Card } from '@apps/web/components/ui/card';
import { ScrollArea } from '@apps/web/components/ui/scroll_area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@apps/web/components/ui/table';
import { getNavigableElementProps } from '@apps/web/lib/navigation_utils';
import { cn } from '@apps/web/lib/utils';
import {
    useAdminDataQualityIssues,
    useAdminDataQualitySummary,
} from '@apps/web/swr_data_fetching/data_fetching_hooks';
import { type DataQualityIssueType } from '@libraries/typescript/api_client';

export const Route = createFileRoute('/_app/admin/data-quality')({
    head: () => ({
        meta: [
            {
                title: 'Data Quality | Phoebe Admin',
            },
        ],
    }),
    component: DataQualityPage,
    validateSearch: z.object({
        organizationId: z.string().optional(),
    }),
});

const ISSUE_TYPE_LABELS: Record<string, string> = {
    missing_phone: 'Missing Phone',
    duplicate_name: 'Duplicate Name',
    no_primary_contact: 'No Primary Contact',
    no_contacts: 'No Contacts',
};

const ISSUE_TYPE_COLORS: Record<string, string> = {
    missing_phone:
        'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
    duplicate_name:
        'bg-orange-100 text-orange-800 dark:bg-orange-950/50 dark:text-orange-300',
    no_primary_contact:
        'bg-yellow-100 text-yellow-800 dark:bg-yellow-950/50 dark:text-yellow-300',
    no_contacts: 'bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300',
};

function DataQualityPage() {
    const { organizationId } = Route.useSearch();

    return (
        <AdminLayoutWithDetails
            listContent={<OrgCardList />}
            detailsContent={<IssueDetails organizationId={organizationId} />}
            defaultListSize={50}
            defaultDetailsSize={50}
        />
    );
}

function OrgCardList() {
    const router = useRouter();
    const { organizationId } = Route.useSearch();

    const { data: summaryResponse, isLoading } = useAdminDataQualitySummary();

    const organizations = summaryResponse?.organizations ?? [];

    const totalIssues = organizations.reduce(
        (sum, org) => sum + org.total_issues,
        0,
    );
    const orgsWithIssues = organizations.filter(
        (org) => org.total_issues > 0,
    ).length;
    const getOrganizationNavigation = (targetOrganizationId: string) => {
        const routeOptions = {
            to: '/admin/data-quality',
            search: { organizationId: targetOrganizationId },
        } as const;
        return {
            href: router.buildLocation(routeOptions).href,
            navigate: () => {
                void router.navigate(routeOptions);
            },
        };
    };

    return (
        <React.Fragment>
            <div className="border-border border-b p-4">
                <h2 className="text-lg font-semibold">Data Quality</h2>
                <p className="text-muted-foreground mt-1 text-sm">
                    {totalIssues} issues across {orgsWithIssues} of{' '}
                    {organizations.length} orgs
                </p>
            </div>
            <ScrollArea className="flex-1 overflow-auto p-4">
                {isLoading ? (
                    <div className="flex h-32 items-center justify-center">
                        <div className="text-muted-foreground flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            <span>Loading…</span>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {organizations.map((org) => (
                            <Card
                                key={org.organization_id}
                                className={cn(
                                    'hover:bg-muted/50 cursor-pointer p-4 transition-colors',
                                    organizationId === org.organization_id &&
                                        'bg-muted/50',
                                )}
                                {...getNavigableElementProps(
                                    getOrganizationNavigation(
                                        org.organization_id,
                                    ),
                                )}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="font-medium">
                                        {org.organization_name}
                                    </div>
                                    {org.total_issues === 0 ? (
                                        <CheckCircle2 className="h-5 w-5 text-green-500 dark:text-green-400" />
                                    ) : (
                                        <Badge
                                            variant="destructive"
                                            className="bg-red-100 text-red-800 dark:bg-red-950/50 dark:text-red-300"
                                        >
                                            {org.total_issues}
                                        </Badge>
                                    )}
                                </div>
                                {org.total_issues > 0 && (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                        {org.missing_phone_count > 0 && (
                                            <IssueChip
                                                icon={Phone}
                                                label="Missing phone"
                                                count={org.missing_phone_count}
                                            />
                                        )}
                                        {org.duplicate_name_count > 0 && (
                                            <IssueChip
                                                icon={Users}
                                                label="Duplicates"
                                                count={org.duplicate_name_count}
                                            />
                                        )}
                                        {org.no_primary_contact_count > 0 && (
                                            <IssueChip
                                                icon={User}
                                                label="No primary"
                                                count={
                                                    org.no_primary_contact_count
                                                }
                                            />
                                        )}
                                        {org.no_contacts_count > 0 && (
                                            <IssueChip
                                                icon={AlertTriangle}
                                                label="No contacts"
                                                count={org.no_contacts_count}
                                            />
                                        )}
                                    </div>
                                )}
                            </Card>
                        ))}
                    </div>
                )}
            </ScrollArea>
        </React.Fragment>
    );
}

function IssueChip({
    icon: Icon,
    label,
    count,
}: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    count: number;
}) {
    return (
        <div className="text-muted-foreground flex items-center gap-1 text-xs">
            <Icon className="h-3 w-3" />
            <span>
                {count} {label}
            </span>
        </div>
    );
}

function IssueDetails({ organizationId }: { organizationId?: string }) {
    const [selectedIssueType, setSelectedIssueType] = useState<
        DataQualityIssueType | undefined
    >(undefined);

    const { data: issuesResponse, isLoading } = useAdminDataQualityIssues(
        organizationId
            ? {
                  organization_id: organizationId,
                  issue_type: selectedIssueType,
                  per_page: 100,
              }
            : undefined,
    );

    const issues = issuesResponse?.issues ?? [];

    if (!organizationId) {
        return (
            <div className="text-muted-foreground flex h-full items-center justify-center">
                Select an organization to view issues
            </div>
        );
    }

    const issueTypes: Array<DataQualityIssueType> = [
        'missing_phone',
        'duplicate_name',
        'no_primary_contact',
        'no_contacts',
    ];

    return (
        <div className="space-y-6">
            <h2 className="text-lg font-semibold">Issues</h2>
            <div className="flex flex-wrap gap-2">
                <Button
                    variant={
                        selectedIssueType === undefined ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => setSelectedIssueType(undefined)}
                >
                    All
                </Button>
                {issueTypes.map((type) => (
                    <Button
                        key={type}
                        variant={
                            selectedIssueType === type ? 'default' : 'outline'
                        }
                        size="sm"
                        onClick={() => setSelectedIssueType(type)}
                    >
                        {ISSUE_TYPE_LABELS[type]}
                    </Button>
                ))}
            </div>
            {isLoading ? (
                <div className="flex h-32 items-center justify-center">
                    <div className="text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading…</span>
                    </div>
                </div>
            ) : issues.length === 0 ? (
                <div className="flex h-32 flex-col items-center justify-center gap-2">
                    <CheckCircle2 className="h-8 w-8 text-green-500 dark:text-green-400" />
                    <div className="text-muted-foreground text-sm">
                        No issues found
                    </div>
                </div>
            ) : (
                <div className="space-y-3">
                    <div className="text-muted-foreground text-sm">
                        {issuesResponse?.total ?? 0} issues
                    </div>
                    <div className="rounded-md border">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Caregiver</TableHead>
                                    <TableHead>Issue</TableHead>
                                    <TableHead className="w-[50px]" />
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {issues.map((issue) => (
                                    <TableRow
                                        key={`${issue.caregiver_id}-${issue.issue_type}`}
                                    >
                                        <TableCell className="font-medium">
                                            {issue.caregiver_name}
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                className={cn(
                                                    'text-xs',
                                                    ISSUE_TYPE_COLORS[
                                                        issue.issue_type
                                                    ],
                                                )}
                                            >
                                                {
                                                    ISSUE_TYPE_LABELS[
                                                        issue.issue_type
                                                    ]
                                                }
                                            </Badge>
                                        </TableCell>
                                        <TableCell>
                                            <Link
                                                to="/admin/caregivers/$caregiver-id"
                                                params={{
                                                    'caregiver-id':
                                                        issue.caregiver_id,
                                                }}
                                                className="text-blue-600 hover:underline dark:text-blue-400"
                                            >
                                                <ExternalLink className="h-4 w-4" />
                                            </Link>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            )}
        </div>
    );
}
